import { FC, memo, useState, useMemo } from "react";
import { useUnit } from "effector-react";
import {
  $UserAddPermissions,
  ACTIONS,
  UserRoleEnum,
  UserPriorityEnum,
} from "@store/user-store";
import { switchCompany } from "@store/company/company-store";
import {
  fetchSpecialistPlans,
  setPlansError,
  setPlansLoading,
} from "@store/specialist-plan-store";
import { CompanyShortDataT } from "@interfaces/company/company";
import { NavLink } from "react-router-dom";
import clsx from "clsx";
import style from "./list.module.scss";
import { InternalStudyListTable } from "@components/tables";
import { ColorfulButton } from "@components/common/common";
import { Search } from "@ui/search";
import useModal from "@modals/modal-hook";
import { useCompanyId } from "@store/company/company-store";
import { useCompaniesQuery } from "@lib/company";
import { useSearch } from "@hooks/useSearch";
import { getCompanyLogo } from "@utils/getCompanyLogo";
import { getTextExcerpt } from "@utils/common-utils";

const InternalList = memo(() => {
  const permissions = useUnit($UserAddPermissions);
  const { open, close } = useModal();

  const companyId = useCompanyId();
  const { companies } = useCompaniesQuery();
  const [selectedCompany, setSelectedCompany] = useState(
    companies !== undefined && companyId !== companies[0].id
  );

  useMemo(() => {
    return setSelectedCompany(
      companies !== undefined &&
        companies.find((e) => e.id === companyId) !== undefined
    );
  }, [companies]);

  const handleCompanyChanging = (companyIdNew: number) => {
    if (!selectedCompany) {
      switchCompany({ id: companyIdNew });
      setSelectedCompany(true);
      setPlansLoading(true);

      fetchSpecialistPlans({
        companies: [companyIdNew],
        groups: null,
        statuses: null,
        count: true,
        startDate: null,
        endDate: null,
        offset: 0,
        limit: 10,
        cb: (err) => {
          if (err) {
            return setPlansError(true);
          }
          setPlansLoading(false);
        },
      });
    }
  };

  const search = useSearch();
  const filterCompanies = (companies: CompanyShortDataT[]) => {
    if (
      !permissions.roleIsIn([
        UserRoleEnum.SuperAdmin,
        UserRoleEnum.SuperAdminMinus,
        UserRoleEnum.Admin,
      ])
    ) {
      return companies.filter((company) => company.tariff > 0);
    }

    return companies;
  };

  return !selectedCompany ? (
    <div style={{ padding: "16px" }}>
      <Search
        options={companies ?? []}
        getOptionLabel={(option) => option.name}
        showDropDown={false}
        placeholder="Поиск по ИНН и названию компании"
        value={search.value}
        onChange={search.onChange}
      />
      <div className={clsx(style.companies_list)}>
        {filterCompanies(companies ?? []).map((company) => (
          <CompanyCard
            key={company.id}
            company={company}
            isSelected={company.id === companyId}
            onSelect={() => handleCompanyChanging(company.id)}
          />
        ))}
      </div>
    </div>
  ) : (
    <div
      className={clsx(
        style.content,
        !permissions.hasPermission(
          ACTIONS.educationalCenter_internalTraining_list_allowedToCreate
        )
          ? style.no_btn
          : ""
      )}
    >
      <div className={style.head}>
        <div className={style.tabs}>
          <div className={style.tabs_active}>Список обучений</div>
          {permissions.hasPermission(
            ACTIONS.educationalCenter_internalTraining_student_allowedToShow
          ) && (
            <NavLink to="/study-center/internal/students">
              Список обучающихся
            </NavLink>
          )}
          {permissions.hasPermission(
            ACTIONS.educationalCenter_internalTraining_list_allowedToShow
          ) && (
            <NavLink to="/study-center/internal/comission">Комиссии</NavLink>
          )}
          {permissions.hasPermission(
            ACTIONS.educationalCenter_internalTraining_programs_allowedToShow
          ) && (
            <NavLink to="/study-center/internal/programs">
              Программы обучений
            </NavLink>
          )}
          {permissions.hasRequiredLevel(UserPriorityEnum.Owner) && (
            <NavLink to="/study-center/internal/courses">Курсы</NavLink>
          )}
        </div>
      </div>
      {permissions.hasPermission(
        ACTIONS.educationalCenter_internalTraining_list_allowedToCreate
      ) && (
        <div className={clsx(style.list_top_panel)}>
          <ColorfulButton
            text={"Добавить обучение"}
            onClick={() =>
              open("CreateInternalListModal", {
                btnText: "Добавить",
                modalData: {
                  modalTitle: "Добавить обучение",
                  editMode: false,
                },
              })
            }
          />
        </div>
      )}
      <InternalStudyListTable />
    </div>
  );
});

type CompanyCardProps = {
  company: CompanyShortDataT;
  isSelected: boolean;
  onSelect: () => void;
};

const CompanyCard: FC<CompanyCardProps> = ({
  company,
  isSelected,
  onSelect,
}) => {
  return (
    <div
      className={clsx(style.company_item, "hover-animation-card", {
        [style.active]: isSelected,
      })}
      onClick={() => onSelect()}
    >
      <img
        src={getCompanyLogo(company.image)}
        alt=""
        className={clsx(style.company_img)}
      />
      <p className={clsx(style.company_info_text)}>
        {getTextExcerpt(
          `${company.name}`,
          document.documentElement.clientWidth > 1124
            ? 25
            : document.documentElement.clientWidth < 480
              ? 13
              : 20
        )}
      </p>
    </div>
  );
};

export default InternalList;
